import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { trackGtmEvent } from './gtm-analytics'
import type { GtmDemoView } from '@/features/gtm/domain/gtm-demo-view'
import { GtmDemoRequestError } from '@/features/gtm/api/gtm-demo-api'
import {
  gtmDemoKeys,
  useCreateGtmDemoRunMutation,
} from '@/features/gtm/api/gtm-demo-queries'
import { resolveGtmDemoView } from '@/features/gtm/domain/gtm-demo-view'
import { trackGtmDemoFailed, trackGtmDemoSubmit } from '@/shared/analytics'

interface UseGtmDemoOptions {
  /** The run exists: the caller opens its page. */
  onCreated: (runId: string) => void
}

export interface GtmDemoController {
  url: string
  setUrl: (url: string) => void
  submit: () => Promise<void>
  reset: () => void
  isSubmitting: boolean
  view: GtmDemoView
}

/**
 * Landing form: starts a demo run. The run itself (stages, polling, results)
 * lives on its own page; here only creation errors are shown.
 */
export function useGtmDemo({
  onCreated,
}: UseGtmDemoOptions): GtmDemoController {
  const queryClient = useQueryClient()
  const [url, setUrlState] = useState('')
  const createMutation = useCreateGtmDemoRunMutation()

  const setUrl = (value: string) => {
    if (createMutation.isError) createMutation.reset()
    setUrlState(value)
  }

  const view = resolveGtmDemoView({
    isStarting: createMutation.isPending,
    startError: createMutation.error,
    runId: undefined,
    run: undefined,
    runError: null,
  })

  const submit = async () => {
    const trimmed = url.trim()
    if (!trimmed || createMutation.isPending) return

    trackGtmDemoSubmit()
    try {
      const created = await createMutation.mutateAsync(trimmed)
      trackGtmEvent('gtm_demo_started', {}, created.id)
      // The create response is the run's first snapshot; its page polls on.
      queryClient.setQueryData(gtmDemoKeys.run(created.id), created)
      onCreated(created.id)
    } catch (error) {
      trackGtmDemoFailed({
        error_code:
          error instanceof GtmDemoRequestError ? error.key : 'internal',
      })
    }
  }

  const reset = () => {
    createMutation.reset()
    setUrlState('')
  }

  return {
    url,
    setUrl,
    submit,
    reset,
    isSubmitting: createMutation.isPending,
    view,
  }
}
